// src/components/layout/AccountSwitcher.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { User, ChevronDown, Check, Plus, X, Loader2, LogIn } from 'lucide-react'
import { apiService } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import toast from 'react-hot-toast'

interface LinkedAccount { id: string; email: string; fullName: string; isCurrent: boolean }

export default function AccountSwitcher() {
  const { user } = useAuth()
  const [open, setOpen] = useState(false)
  const [accounts, setAccounts] = useState<LinkedAccount[]>([])
  const [showAdd, setShowAdd] = useState(false)
  const [addEmail, setAddEmail] = useState('')
  const [addPassword, setAddPassword] = useState('')
  const [adding, setAdding] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [unlinkTarget, setUnlinkTarget] = useState<LinkedAccount | null>(null)
  const [unlinking, setUnlinking] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    try { setAccounts(await apiService.getLinkedAccounts()) } catch { /* sessiz */ }
  }, [])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  const doSwitch = async (id: string) => {
    if (switching) return
    setSwitching(true)
    try {
      const res = await apiService.switchAccount(id)
      apiService.saveAuthData(res)
      window.location.assign('/') // yeni hesapla taze yükle (çık-gir yok)
    } catch (e: any) {
      toast.error(e.message || 'Geçiş başarısız')
      setSwitching(false)
    }
  }

  const doAdd = async () => {
    if (!addEmail.trim() || !addPassword) { toast.error('E-posta ve şifre gerekli'); return }
    setAdding(true)
    try {
      await apiService.linkAccount(addEmail.trim(), addPassword)
      toast.success('Hesap eklendi')
      setShowAdd(false); setAddEmail(''); setAddPassword('')
      load()
    } catch (e: any) {
      toast.error(e.message || 'Hesap eklenemedi')
    } finally { setAdding(false) }
  }

  const confirmUnlink = async () => {
    if (!unlinkTarget) return
    const email = unlinkTarget.email
    setUnlinking(true)
    try {
      await apiService.unlinkAccount(unlinkTarget.id)
      setUnlinkTarget(null)
      toast.success(`${email} bağlantısı kaldırıldı`)
      load()
    } catch { toast.error('Bağlantı kaldırılamadı') }
    finally { setUnlinking(false) }
  }

  const others = accounts.filter(a => !a.isCurrent)
  const initial = (s?: string) => (s || '?').charAt(0).toUpperCase()

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center space-x-3 hover:bg-gray-50 rounded-lg p-1 transition-colors"
      >
        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary-500 to-orange-500 flex items-center justify-center shrink-0">
          <User className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="font-medium text-gray-800 truncate" title={user?.email}>{user?.email || 'Kullanıcı'}</p>
          <p className="text-xs text-gray-400">Hesap değiştir</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden z-50">
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 border-b border-gray-100">Hesaplar</div>
          <div className="max-h-60 overflow-y-auto">
            <div className="flex items-center gap-2 px-3 py-2 bg-orange-50">
              <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs shrink-0">{initial(user?.fullName || user?.email)}</div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800 truncate">{user?.fullName || user?.email}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
              <Check className="w-4 h-4 text-orange-600 shrink-0" />
            </div>
            {others.map(a => (
              <button key={a.id} onClick={() => doSwitch(a.id)} className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left group">
                <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-white text-xs shrink-0">{initial(a.fullName || a.email)}</div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-800 truncate">{a.fullName || a.email}</p>
                  <p className="text-xs text-gray-500 truncate">{a.email}</p>
                </div>
                <span onClick={(e) => { e.stopPropagation(); setUnlinkTarget(a) }} className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded p-1 shrink-0" title="Bağlantıyı kaldır">
                  <X className="w-4 h-4" />
                </span>
              </button>
            ))}
          </div>
          <button onClick={() => { setShowAdd(true); setOpen(false) }} className="w-full flex items-center gap-2 px-3 py-2.5 border-t border-gray-100 text-sm text-orange-600 hover:bg-orange-50 font-medium">
            <Plus className="w-4 h-4" /> Hesap Ekle
          </button>
        </div>
      )}

      {switching && createPortal(
        <div className="fixed inset-0 bg-white/60 flex items-center justify-center z-[100]">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>,
        document.body
      )}

      {showAdd && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">Hesap Ekle</h3>
              <button onClick={() => setShowAdd(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-gray-500 mb-3">Eklemek istediğiniz hesabın e-posta ve şifresini bir kez girin; sonra şifresiz geçiş yapabilirsiniz.</p>
            <input type="email" value={addEmail} onChange={e => setAddEmail(e.target.value)} placeholder="E-posta" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2" />
            <input type="password" value={addPassword} onChange={e => setAddPassword(e.target.value)} placeholder="Şifre" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4" />
            <button onClick={doAdd} disabled={adding} className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-lg py-2 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50">
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />} Ekle
            </button>
          </div>
        </div>,
        document.body
      )}

      {unlinkTarget && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6 shadow-xl">
            <h3 className="font-bold text-gray-800 mb-2">Bağlantıyı Kaldır</h3>
            <p className="text-sm text-gray-600 mb-5">
              <span className="font-medium break-all">{unlinkTarget.email}</span> hesabının bağlantısını kaldırmak istediğinize emin misiniz?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setUnlinkTarget(null)} disabled={unlinking}
                className="flex-1 border border-gray-200 rounded-lg py-2 text-sm hover:bg-gray-50 disabled:opacity-50">
                İptal
              </button>
              <button onClick={confirmUnlink} disabled={unlinking}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white rounded-lg py-2 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50">
                {unlinking ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Kaldır
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
